/**
 * Admin Academy Hooks
 *
 * Hooks for managing academy courses, exams, and questions in admin panel.
 */

import { useQuery, useMutation } from "urql";

// =============================================================================
// QUERIES
// =============================================================================

const AdminAcademyCoursesDocument = `
  query AdminAcademyCourses {
    adminAcademyCourses {
      id
      externalId
      title
      description
      externalUrl
      imageUrl
      sortOrder
      isActive
      createdAt
      exam {
        id
        name
        questionsCount
      }
    }
  }
`;

const AdminAcademyCourseDocument = `
  query AdminAcademyCourse($id: ID!) {
    adminAcademyCourse(id: $id) {
      id
      externalId
      title
      description
      externalUrl
      imageUrl
      sortOrder
      isActive
      createdAt
      exam {
        id
        name
        description
        passingScore
        questionsCount
      }
    }
  }
`;

const AdminExamQuestionsDocument = `
  query AdminExamQuestions($examId: ID!) {
    adminExamQuestions(examId: $examId) {
      id
      text
      type
      sortOrder
      options {
        id
        text
      }
      correctAnswer
    }
  }
`;

const AdminCourseAnalyticsDocument = `
  query AdminCourseAnalytics($courseId: ID!) {
    adminCourseAnalytics(courseId: $courseId) {
      totalVisitors
      examStartedCount
      examPassedCount
      averageWrongAttempts
      passRate
      visitors {
        account {
          id
          firstName
          lastName
          username
        }
        visitCount
        firstVisit
        lastVisit
      }
      inProgress {
        account {
          id
          firstName
          lastName
          username
        }
        attempt {
          id
        }
        startedAt
        currentQuestion
        totalQuestions
        wrongAttempts
      }
      passed {
        account {
          id
          firstName
          lastName
          username
        }
        attempt {
          id
          score
          maxScore
          completedAt
        }
        passedAt
        wrongAttempts
      }
    }
  }
`;

// =============================================================================
// MUTATIONS
// =============================================================================

const CreateAcademyCourseDocument = `
  mutation CreateAcademyCourse($input: CreateAcademyCourseInput!) {
    createAcademyCourse(input: $input) {
      id
      externalId
      title
      description
      externalUrl
      imageUrl
      sortOrder
      isActive
    }
  }
`;

const UpdateAcademyCourseDocument = `
  mutation UpdateAcademyCourse($id: ID!, $input: UpdateAcademyCourseInput!) {
    updateAcademyCourse(id: $id, input: $input) {
      id
      externalId
      title
      description
      externalUrl
      imageUrl
      sortOrder
      isActive
    }
  }
`;

const DeleteAcademyCourseDocument = `
  mutation DeleteAcademyCourse($id: ID!) {
    deleteAcademyCourse(id: $id)
  }
`;

const CreateCourseExamDocument = `
  mutation CreateCourseExam($input: CreateCourseExamInput!) {
    createCourseExam(input: $input) {
      id
      name
      description
      passingScore
    }
  }
`;

const UpdateCourseExamDocument = `
  mutation UpdateCourseExam($id: ID!, $input: UpdateCourseExamInput!) {
    updateCourseExam(id: $id, input: $input) {
      id
      name
      description
      passingScore
    }
  }
`;

const CreateExamQuestionDocument = `
  mutation CreateExamQuestion($input: CreateExamQuestionInput!) {
    createExamQuestion(input: $input) {
      id
      text
      type
      sortOrder
      options {
        id
        text
      }
      correctAnswer
    }
  }
`;

const UpdateExamQuestionDocument = `
  mutation UpdateExamQuestion($id: ID!, $input: UpdateExamQuestionInput!) {
    updateExamQuestion(id: $id, input: $input) {
      id
      text
      type
      sortOrder
      options {
        id
        text
      }
      correctAnswer
    }
  }
`;

const DeleteExamQuestionDocument = `
  mutation DeleteExamQuestion($id: ID!) {
    deleteExamQuestion(id: $id)
  }
`;

const ReorderExamQuestionsDocument = `
  mutation ReorderExamQuestions($examId: ID!, $questionIds: [ID!]!) {
    reorderExamQuestions(examId: $examId, questionIds: $questionIds)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface AdminCourseExamSummary {
  id: string;
  name: string;
  questionsCount: number;
}

export interface AdminAcademyCourse {
  id: string;
  externalId: string | null;
  title: string;
  description: string | null;
  externalUrl: string | null;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  exam: AdminCourseExamSummary | null;
}

export interface AdminCourseExam {
  id: string;
  name: string;
  description: string | null;
  passingScore: number;
  questionsCount: number;
}

export interface AdminCourseWithExam extends Omit<AdminAcademyCourse, "exam"> {
  exam: AdminCourseExam | null;
}

export interface ExamQuestionOption {
  id: string;
  text: string;
}

export interface AdminExamQuestion {
  id: string;
  text: string;
  type: "SINGLE_CHOICE" | "MULTIPLE_CHOICE" | "TEXT";
  sortOrder: number;
  options: ExamQuestionOption[];
  correctAnswer: string;
}

interface AccountInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

export interface CourseVisitorInfo {
  account: AccountInfo;
  visitCount: number;
  firstVisit: string;
  lastVisit: string;
}

export interface ExamInProgressInfo {
  account: AccountInfo;
  attempt: { id: string };
  startedAt: string;
  currentQuestion: number;
  totalQuestions: number;
  wrongAttempts: number;
}

export interface ExamPassedInfo {
  account: AccountInfo;
  attempt: {
    id: string;
    score: number;
    maxScore: number;
    completedAt: string;
  };
  passedAt: string;
  wrongAttempts: number;
}

export interface CourseAnalytics {
  totalVisitors: number;
  examStartedCount: number;
  examPassedCount: number;
  averageWrongAttempts: number;
  passRate: number;
  visitors: CourseVisitorInfo[];
  inProgress: ExamInProgressInfo[];
  passed: ExamPassedInfo[];
}

export interface CreateCourseInput {
  externalId?: string;
  title: string;
  description?: string;
  externalUrl?: string;
  imageUrl?: string;
  sortOrder?: number;
  // Note: isActive is not supported in create - new courses are active by default
}

export interface UpdateCourseInput {
  externalId?: string | null;
  title?: string;
  description?: string | null;
  externalUrl?: string | null;
  imageUrl?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateExamInput {
  courseId: string;
  name: string;
  description?: string;
  passingScore?: number;
}

export interface UpdateExamInput {
  name?: string;
  description?: string | null;
  passingScore?: number;
}

export interface CreateQuestionInput {
  examId: string;
  text: string;
  type?: "SINGLE_CHOICE" | "MULTIPLE_CHOICE" | "TEXT";
  options: Array<{ id: string; text: string }>;
  correctAnswer: string;
}

export interface UpdateQuestionInput {
  text?: string;
  type?: "SINGLE_CHOICE" | "MULTIPLE_CHOICE" | "TEXT";
  options?: Array<{ id: string; text: string }>;
  correctAnswer?: string;
}

// =============================================================================
// HOOKS - QUERIES
// =============================================================================

export function useAdminCourses() {
  const [result, reexecuteQuery] = useQuery<{
    adminAcademyCourses: AdminAcademyCourse[];
  }>({
    query: AdminAcademyCoursesDocument,
  });

  return {
    courses: result.data?.adminAcademyCourses ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

export function useAdminCourse(courseId: string) {
  const [result, reexecuteQuery] = useQuery<{
    adminAcademyCourse: AdminCourseWithExam | null;
  }>({
    query: AdminAcademyCourseDocument,
    variables: { id: courseId },
    pause: !courseId,
  });

  return {
    course: result.data?.adminAcademyCourse ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

export function useAdminExamQuestions(examId: string) {
  const [result, reexecuteQuery] = useQuery<{
    adminExamQuestions: AdminExamQuestion[];
  }>({
    query: AdminExamQuestionsDocument,
    variables: { examId },
    pause: !examId,
  });

  return {
    questions: result.data?.adminExamQuestions ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

export function useAdminCourseAnalytics(courseId: string) {
  const [result, reexecuteQuery] = useQuery<{
    adminCourseAnalytics: CourseAnalytics;
  }>({
    query: AdminCourseAnalyticsDocument,
    variables: { courseId },
    pause: !courseId,
  });

  return {
    analytics: result.data?.adminCourseAnalytics ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

// =============================================================================
// HOOKS - MUTATIONS
// =============================================================================

export function useCreateCourse() {
  const [result, executeMutation] = useMutation<{
    createAcademyCourse: AdminAcademyCourse;
  }>(CreateAcademyCourseDocument);

  return {
    createCourse: async (input: CreateCourseInput) => {
      const response = await executeMutation({ input });
      if (response.error) throw response.error;
      return response.data?.createAcademyCourse ?? null;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useUpdateCourse() {
  const [result, executeMutation] = useMutation<{
    updateAcademyCourse: AdminAcademyCourse;
  }>(UpdateAcademyCourseDocument);

  return {
    updateCourse: async (id: string, input: UpdateCourseInput) => {
      const response = await executeMutation({ id, input });
      if (response.error) throw response.error;
      return response.data?.updateAcademyCourse ?? null;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useDeleteCourse() {
  const [result, executeMutation] = useMutation<{
    deleteAcademyCourse: boolean;
  }>(DeleteAcademyCourseDocument);

  return {
    deleteCourse: async (id: string) => {
      const response = await executeMutation({ id });
      if (response.error) throw response.error;
      return response.data?.deleteAcademyCourse ?? false;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useCreateExam() {
  const [result, executeMutation] = useMutation<{
    createCourseExam: AdminCourseExam;
  }>(CreateCourseExamDocument);

  return {
    createExam: async (input: CreateExamInput) => {
      const response = await executeMutation({ input });
      if (response.error) throw response.error;
      return response.data?.createCourseExam ?? null;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useUpdateExam() {
  const [result, executeMutation] = useMutation<{
    updateCourseExam: AdminCourseExam;
  }>(UpdateCourseExamDocument);

  return {
    updateExam: async (id: string, input: UpdateExamInput) => {
      const response = await executeMutation({ id, input });
      if (response.error) throw response.error;
      return response.data?.updateCourseExam ?? null;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useCreateQuestion() {
  const [result, executeMutation] = useMutation<{
    createExamQuestion: AdminExamQuestion;
  }>(CreateExamQuestionDocument);

  return {
    createQuestion: async (input: CreateQuestionInput) => {
      const response = await executeMutation({ input });
      if (response.error) throw response.error;
      return response.data?.createExamQuestion ?? null;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useUpdateQuestion() {
  const [result, executeMutation] = useMutation<{
    updateExamQuestion: AdminExamQuestion;
  }>(UpdateExamQuestionDocument);

  return {
    updateQuestion: async (id: string, input: UpdateQuestionInput) => {
      const response = await executeMutation({ id, input });
      if (response.error) throw response.error;
      return response.data?.updateExamQuestion ?? null;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useDeleteQuestion() {
  const [result, executeMutation] = useMutation<{
    deleteExamQuestion: boolean;
  }>(DeleteExamQuestionDocument);

  return {
    deleteQuestion: async (id: string) => {
      const response = await executeMutation({ id });
      if (response.error) throw response.error;
      return response.data?.deleteExamQuestion ?? false;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

export function useReorderQuestions() {
  const [result, executeMutation] = useMutation<{
    reorderExamQuestions: boolean;
  }>(ReorderExamQuestionsDocument);

  return {
    reorderQuestions: async (examId: string, questionIds: string[]) => {
      const response = await executeMutation({ examId, questionIds });
      if (response.error) throw response.error;
      return response.data?.reorderExamQuestions ?? false;
    },
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
